import React, { useState, useEffect } from "react";
import { Image } from "antd";

import styles from "./index.module.css";
import LOGO from "../../assets/logo_blacktext.png";
import BLACK_LOGO from "../../assets/logo_whitetext.png";
import {
  GiftOutlined,
  UserOutlined,
  EnvironmentOutlined,
  SearchOutlined,
  SoundOutlined,
} from "@ant-design/icons";

export default function PreviewBanner({ src, useNewDesign = false }) {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Check initial theme
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute("data-theme");
      setIsDarkMode(theme === "dark");
    };

    checkTheme();

    // Listen for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Add debugging
  console.log("PreviewBanner src:", src);

  // If using new design with backdrop, show the new layout
  if (useNewDesign) {
    return (
      <div className={styles.mobilePreview}>
        <div
          style={{
            position: "absolute",
            top: "-30px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "18px",
            color: "#666",
            fontWeight: "700",
            textAlign: "center",
            zIndex: 10,
          }}
        >
          Mobile App Preview
        </div>

        <div className={styles.phoneFrame}>
          {/* Status Bar */}
          <div className={styles.statusBar}>
            <span className={styles.statusTime}>9:41</span>
          </div>

          {/* Header with gradient backdrop */}
          <div className={styles.newMobileHeader}>
            <div className={styles.locationSection}>
              <EnvironmentOutlined className={styles.locationIcon} />
              <div className={styles.locationInfo}>
                <span className={styles.deliveryTime}>8 mins</span>
                <span className={styles.locationText}>Hiranandani, Thane</span>
              </div>
            </div>
            <div className={styles.headerActions}>
              <GiftOutlined className={styles.headerIcon} />
              <UserOutlined className={styles.headerIcon} />
            </div>
          </div>

          {/* Search Bar with Daily Deals */}
          <div className={styles.newSearchSection}>
            <div className={styles.searchRow}>
              <div className={styles.newSearchBar}>
                <SearchOutlined className={styles.newSearchIcon} />
                <span className={styles.newSearchPlaceholder}>
                  Search for products...
                </span>
                <SoundOutlined className={styles.voiceIcon} />
              </div>
              <div className={styles.dailyDeals}>
                <span className={styles.dailyDealsPercent}>%</span>
                <span className={styles.dailyDealsText}>
                  DAILY
                  <br />
                  DEALS
                </span>
              </div>
            </div>
          </div>

          {/* Backdrop Image (390x120px - Green area) */}
          <div className={styles.backdropSection}>
            <div className={styles.backdropPlaceholder}>
              <div className={styles.placeholderText}>
                390 x 120 px. backdrop img
              </div>
            </div>
          </div>

          {/* Banner Section (358x170px - Blue area) */}
          <div className={styles.newBannerSection}>
            {src ? (
              <Image
                className={styles.newBannerImage}
                src={src}
                alt="Banner"
                preview={false}
              />
            ) : (
              <div className={styles.newBannerPlaceholder}>
                <div className={styles.placeholderText}>358 x 170 px banner</div>
              </div>
            )}
          </div>

          {/* Category Section - kept simple/greyish */}
          <div className={styles.categorySection}>
            <h3 className={styles.categoryHeading}>Shop by Category</h3>
            <div className={styles.categoryGrid}>
              {Array.from({ length: 12 }, (_, i) => (
                <div key={i} className={styles.categoryCard}>
                  <div className={styles.categoryTextGroup}>
                    <div className={styles.categoryName}></div>
                    <div className={styles.categorySubtext}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced fallback for when src is not provided
  if (!src) {
    return (
      <div className={styles.mobilePreview}>
        {/* Mobile Preview Indicator */}
        <div
          style={{
            position: "absolute",
            top: "-30px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "18px",
            color: "#666",
            fontWeight: "700",
            textAlign: "center",
            zIndex: 10,
          }}
        >
          Mobile App Preview
        </div>

        <div className={styles.phoneFrame}>
          {/* Header */}
          <div className={styles.mobileHeader}>
            <img
              className={styles.logo}
              src={isDarkMode ? BLACK_LOGO : LOGO}
              alt="Rapsap Logo"
            />
            <div className={styles.headerActions}>
              <div className={styles.headerIcon}></div>
              <div className={styles.headerIcon}></div>
            </div>
          </div>

          {/* Search Bar */}
          <div className={styles.searchSection}>
            <div className={styles.searchBar}>
              <div className={styles.searchIcon}>
                <svg viewBox="0 0 24 24" fill="none">
                  <circle
                    cx="11"
                    cy="11"
                    r="8"
                    stroke="#9CA3AF"
                    strokeWidth="1.5"
                  />
                  <path
                    d="m21 21-4.35-4.35"
                    stroke="#9CA3AF"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>
              <div className={styles.searchPlaceholder}></div>
            </div>
          </div>

          {/* Banner Placeholder Section */}
          <div className={styles.bannerSection}>
            <div
              style={{
                width: "358px",
                height: "70px",
                border: "2px dashed #d9d9d9",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  color: "#999",
                  textAlign: "center",
                  lineHeight: "1.2",
                }}
              >
                Upload banner image
                <br />
                (358 x 70 pixels)
              </div>
            </div>
          </div>

          {/* Category Section */}
          <div className={styles.categorySection}>
            <h3 className={styles.categoryHeading}>Shop by Category</h3>
            <div className={styles.categoryGrid}>
              {Array.from({ length: 12 }, (_, i) => (
                <div key={i} className={styles.categoryCard}>
                  <div className={styles.categoryTextGroup}>
                    <div className={styles.categoryName}></div>
                    <div className={styles.categorySubtext}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mobilePreview}>
      {/* Mobile Preview Indicator */}
      <div
        style={{
          position: "absolute",
          top: "-30px",
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: "18px",
          color: "#666",
          fontWeight: "700",
          textAlign: "center",
          zIndex: 10,
        }}
      >
        Mobile App Preview
      </div>

      {/* Mobile Phone Frame */}
      <div className={styles.phoneFrame}>
        {/* Header */}
        <div className={styles.mobileHeader}>
          <img
            className={styles.logo}
            src={isDarkMode ? BLACK_LOGO : LOGO}
            alt="Rapsap Logo"
          />
          <div className={styles.headerActions}>
            <div className={styles.headerIcon}></div>
            <div className={styles.headerIcon}></div>
          </div>
        </div>

        {/* Search Bar */}
        <div className={styles.searchSection}>
          <div className={styles.searchBar}>
            <div className={styles.searchIcon}>
              <svg viewBox="0 0 24 24" fill="none">
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                />
                <path d="m21 21-4.35-4.35" stroke="#9CA3AF" strokeWidth="1.5" />
              </svg>
            </div>
            <div className={styles.searchPlaceholder}></div>
          </div>
        </div>

        {/* Banner Section - Just the image */}
        <div className={styles.bannerSection}>
          <Image
            className={styles.bannerImage}
            src={src}
            alt="Banner"
            preview={false}
            onError={(e) => {
              console.error("Banner image failed to load:", src);
              console.error("Error details:", e);
            }}
            onLoad={() => {
              console.log("Banner image loaded successfully:", src);
            }}
          />
        </div>

        {/* Category Section */}
        <div className={styles.categorySection}>
          <h3 className={styles.categoryHeading}>Shop by Category</h3>
          <div className={styles.categoryGrid}>
            {Array.from({ length: 12 }, (_, i) => (
              <div key={i} className={styles.categoryCard}>
                <div className={styles.categoryTextGroup}>
                  <div className={styles.categoryName}></div>
                  <div className={styles.categorySubtext}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
